import { useQuery } from '@tanstack/react-query';
import { apiHelpers } from '../services/api';

/**
 * Hook to fetch and provide AI feature credit costs
 * Returns a costsMap object with feature keys as keys
 * 
 * Usage:
 * const { getCost, isLoading, formatCost } = useCreditCosts();
 * const chartBuilderCost = getCost('chart_builder'); // Returns 10 (or undefined if not found)
 * const formatted = formatCost('chart_builder'); // Returns "(10 Cr)" or "" if not found/loading
 */
export function useCreditCosts() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['aiFeatureCosts'],
    queryFn: async () => {
      try {
        console.log('[useCreditCosts] Fetching AI feature costs...');
        const response = await apiHelpers.getAIFeatureCosts();
        console.log('[useCreditCosts] Received data:', response.data);
        return response.data;
      } catch (err) {
        console.error('[useCreditCosts] Error fetching costs:', err);
        throw err;
      }
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
    retry: 1
  });
  
  const costsMap = data?.costsMap || {};
  
  // Log when costs are loaded
  if (data && Object.keys(costsMap).length > 0) {
    console.log('[useCreditCosts] Costs map loaded with', Object.keys(costsMap).length, 'features');
  }
  
  // Log errors
  if (error) {
    console.error('[useCreditCosts] Query error:', error);
  }
  
  /**
   * Get the credit cost for a specific feature
   * @param {string} featureKey - The feature key (e.g., 'chart_builder', 'ai_chat')
   * @returns {number|undefined} The credit cost or undefined if not found
   */
  const getCost = (featureKey) => {
    return costsMap[featureKey]?.cost;
  };
  
  /**
   * Format the credit cost for display on buttons
   * @param {string} featureKey - The feature key
   * @param {object} options - Formatting options
   * @param {boolean} options.showParens - Whether to wrap in parentheses (default: true)
   * @param {string} options.suffix - Suffix to add (default: ' Cr')
   * @returns {string} Formatted string like "(10 Cr)" or empty string if loading/not found
   */
  const formatCost = (featureKey, options = {}) => {
    const { showParens = true, suffix = ' Cr' } = options;
    
    if (isLoading) return '';
    
    const cost = getCost(featureKey);
    if (cost === undefined) return '';
    
    const formatted = `${cost}${suffix}`;
    return showParens ? `(${formatted})` : formatted;
  };
  
  /**
   * Get full feature info including name and description
   * @param {string} featureKey - The feature key
   * @returns {object|undefined} Feature info object or undefined
   */
  const getFeatureInfo = (featureKey) => {
    return costsMap[featureKey];
  };
  
  return {
    costsMap,
    getCost,
    formatCost,
    getFeatureInfo,
    isLoading,
    error,
    features: data?.features || []
  };
}

/**
 * Component to display credit cost badge
 * Use this for inline display on buttons
 */
export function CreditCostBadge({ featureKey, className = '' }) {
  const { formatCost, isLoading } = useCreditCosts();
  
  if (isLoading) return null;
  
  const costText = formatCost(featureKey);
  if (!costText) return null;
  
  return (
    <span className={`text-xs opacity-75 ${className}`}>
      {costText}
    </span>
  );
}

export default useCreditCosts;
